import { Component, Host, h, Element, Prop, Event, EventEmitter, Watch } from '@stencil/core';
import runWhenIdle from '../../utils/runWhenIdle';

export interface AhaSearchField extends HTMLAhaSearchFieldElement {}

/**
 * Input component for automatic search fields. Emits a search event for you to use in the search.
 */
@Component({
  tag: 'aha-search-field',
  styleUrl: 'aha-search-field.css',
  shadow: { delegatesFocus: true },
})
export class AhaSearchField {
  static formAssociated = true;

  private inputField!: HTMLInputElement;

  private clearField(event: MouseEvent) {
    if (!event.currentTarget) return;

    this.loading = false;
    this.value = '';
    this.doSubmit();
    this.throttledSearch.cancel();
    // Firing the input event on the clear press makes it easy for most
    // consumers to just listen to the input event for all changes.
    this.searchFieldInputEvent.emit(this.host);
    this.inputField.focus();
  }

  private unthrottledSearch = () => {
    this.searchFieldInputEvent.emit(this.host);
  };

  // Throttle the search to prevent UI jank. The "leading" option is `false` to prevent react-based searches from firing on the initial input. If the react component's listener takes a long time to run, the leading option could cause the input change to lag showing up in input field until the search results are returned.
  private throttledSearch = runWhenIdle(() => this.unthrottledSearch(), 1000, {
    leading: false,
  });

  private querySearch = (event: any) => {
    this.value = event.target.value;
    if (this.throttled) {
      this.throttledSearch();
    } else {
      this.unthrottledSearch();
    }

    event.stopPropagation();
  };

  private submitSearchOnEnter(event: any) {
    if (event.key === 'Enter') {
      event.currentTarget.getRootNode().host.doSubmit();
    }
  }

  private doSubmit() {
    this.searchFieldChangeEvent.emit(this);
  }

  // These events raise a compilation warning but it's fine. There was an issue
  // opened about this here: https://github.com/ionic-team/stencil/issues/2715.
  // We want to mimick the native events so it's easier to use these in React
  // and jest. Custom events are harder to use in React and impossible with
  // jest. Why shouldn't they mimick native dom events?
  @Event({
    eventName: 'input',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  searchFieldInputEvent!: EventEmitter;

  @Event({
    eventName: 'change',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  searchFieldChangeEvent!: EventEmitter;

  /**
   * Whether or not the first input, select, textarea in the body of the <aha-search-field>
   * component should be focused into when the component is done being mounted
   *
   * NOTE: This should only be used when needed. Autofocus is typically an
   * accessibility issue.
   */
  @Prop({ reflect: true }) autofocus!: boolean;

  /**
   * Value entered in the search box when it is first created
   */
  @Prop({ reflect: true }) value: any = '';
  @Watch('value')
  setElementInternalsValue() {
    // Set the ElementInternals form value to the value of the component. Converts `undefined` and `null` values to a string.
    this.elementInternals?.setFormValue(String(this.value ?? ''));
  }

  /**
   * True if we are in a loading state, false otherwise
   */
  @Prop({ reflect: true }) loading?: boolean;

  /**
   * Placeholder text for input
   */
  @Prop({ reflect: true }) placeholder?: string;

  /**
   * Whether to throttle the search or not
   */
  @Prop({ reflect: true }) throttled: boolean = true;

  private elementInternals?: ElementInternals;

  connectedCallback() {
    this.submitSearchOnEnter = this.submitSearchOnEnter.bind(this);
    this.clearField = this.clearField.bind(this);
  }

  componentDidLoad() {
    // Attach the form internals to the host element so that an external form can be automatically access the form data in the input field in the shadowDOM.
    this.elementInternals = this.host.attachInternals?.();

    // Sets the default value for the elementInternals form value.
    this.setElementInternalsValue();

    // Focus on <aha-search-field>'s first input element if the `autofocus` attribute is set.
    if (this.autofocus) {
      this.host.focus();
    }
  }

  @Element() host!: HTMLAhaSearchFieldElement;
  render() {
    return (
      <Host role="search" aria-label="Search">
        <slot name="icon">
          <fa-icon id="magnifying-glass" class="fa-regular fa-magnifying-glass" />
        </slot>
        <input
          type="search"
          onInput={this.querySearch}
          onKeyPress={this.submitSearchOnEnter}
          ref={ref => (this.inputField = ref as HTMLInputElement)}
          autofocus={this.autofocus}
          autoComplete="off"
          placeholder={this.placeholder}
          value={this.value}
        />
        <button type="button" aria-label="Clear search field" onClick={this.clearField}>
          {this.loading ? (
            <aha-spinner opacity={1} aria-label="Loading" />
          ) : (
            <div id="cancel-icons">
              <fa-icon class="fa-regular fa-circle-xmark" />
            </div>
          )}
        </button>
      </Host>
    );
  }
}
